import { z } from 'zod';
import { getLocale } from './format.js';

/**
 * Prepaid-balance billing, which is what VOIP resale actually needs: every call
 * or service charge debits a customer balance in real time, and the alerting
 * engine watches balances the same way it watches CPU.
 *
 * Money is stored in minor units (piastres/cents) as integers. Never floats.
 */

export const accountSchema = z.object({
  id: z.string(),
  customerId: z.string(),
  name: z.string(),
  currency: z.string().length(3).default('USD'),
  /** Current balance in minor units. Negative = customer owes us. */
  balanceMinor: z.number().int().default(0),
  /** Allowed overdraft before service is cut, in minor units. */
  creditLimitMinor: z.number().int().default(0),
  /** Fire a `balance.low` alert at or below this. */
  lowBalanceThresholdMinor: z.number().int().default(0),
  /** Postpaid accounts are invoiced monthly instead of debited live. */
  postpaid: z.boolean().default(false),
  suspended: z.boolean().default(false),
  createdAt: z.number(),
  updatedAt: z.number(),
});
export type Account = z.infer<typeof accountSchema>;

export const TRANSACTION_TYPES = [
  'topup',
  'call_charge',
  'service_charge',
  'adjustment',
  'refund',
  'invoice_payment',
] as const;
export type TransactionType = (typeof TRANSACTION_TYPES)[number];

export const transactionSchema = z.object({
  id: z.string(),
  accountId: z.string(),
  type: z.enum(TRANSACTION_TYPES),
  /** Signed minor units: negative debits the balance. */
  amountMinor: z.number().int(),
  balanceAfterMinor: z.number().int(),
  currency: z.string().length(3),
  description: z.string().default(''),
  /** Links a call_charge back to the CDR that produced it. */
  referenceType: z.string().nullable().default(null),
  referenceId: z.string().nullable().default(null),
  /** Idempotency key — a repeated key is a no-op, so retries never double-charge. */
  idempotencyKey: z.string().nullable().default(null),
  createdBy: z.string().nullable().default(null),
  createdAt: z.number(),
});
export type Transaction = z.infer<typeof transactionSchema>;

export const rateSchema = z.object({
  id: z.string(),
  /** Rate plan this belongs to; customers are assigned a plan. */
  planId: z.string(),
  /** E.164 dialing prefix, longest match wins. */
  prefix: z.string(),
  destination: z.string(),
  /** Minor units per minute. */
  rateMinorPerMin: z.number().int(),
  /** First increment charged in seconds, then per `increment` seconds. */
  setupFeeMinor: z.number().int().default(0),
  minDurationSec: z.number().int().default(1),
  incrementSec: z.number().int().default(1),
  createdAt: z.number(),
  updatedAt: z.number(),
});
export type Rate = z.infer<typeof rateSchema>;

export const invoiceSchema = z.object({
  id: z.string(),
  number: z.string(),
  customerId: z.string(),
  currency: z.string().length(3),
  periodStart: z.number(),
  periodEnd: z.number(),
  subtotalMinor: z.number().int(),
  taxMinor: z.number().int().default(0),
  totalMinor: z.number().int(),
  state: z.enum(['draft', 'issued', 'paid', 'overdue', 'void']).default('draft'),
  dueAt: z.number().nullable().default(null),
  paidAt: z.number().nullable().default(null),
  lines: z
    .array(
      z.object({
        description: z.string(),
        quantity: z.number(),
        unitPriceMinor: z.number().int(),
        totalMinor: z.number().int(),
      }),
    )
    .default([]),
  /** Letterhead used when this invoice is rendered/emailed; null falls back to the default template. */
  templateId: z.string().nullable().default(null),
  /** Last time the formatted invoice was successfully emailed to the client. */
  sentAt: z.number().nullable().default(null),
  /** Set when the last delivery attempt failed (no contact email, SMTP error); cleared on a successful send. */
  sendError: z.string().nullable().default(null),
  createdAt: z.number(),
  updatedAt: z.number(),
});
export type Invoice = z.infer<typeof invoiceSchema>;

/**
 * A reusable letterhead applied when an invoice is rendered/emailed — company
 * identity, terms and footer text, kept out of code so an operator can change
 * them without a redeploy. Several can exist (e.g. a different entity or
 * brand issuing invoices); exactly one is marked default.
 */
export const invoiceTemplateSchema = z.object({
  id: z.string(),
  name: z.string().min(1),
  isDefault: z.boolean().default(false),
  companyName: z.string().default(''),
  companyAddress: z.string().default(''),
  companyEmail: z.string().default(''),
  companyPhone: z.string().default(''),
  logoUrl: z.string().nullable().default(null),
  accentColor: z.string().default('#2563eb'),
  footerNote: z.string().default(''),
  terms: z.string().default(''),
  createdAt: z.number(),
  updatedAt: z.number(),
});
export type InvoiceTemplate = z.infer<typeof invoiceTemplateSchema>;

export const invoiceTemplateCreateSchema = invoiceTemplateSchema
  .omit({ id: true, createdAt: true, updatedAt: true })
  .partial({
    isDefault: true,
    companyName: true,
    companyAddress: true,
    companyEmail: true,
    companyPhone: true,
    logoUrl: true,
    accentColor: true,
    footerNote: true,
    terms: true,
  });
export type InvoiceTemplateCreate = z.infer<typeof invoiceTemplateCreateSchema>;

export const PAYMENT_METHODS = ['bank_transfer', 'cash', 'card', 'cheque', 'other'] as const;
export type PaymentMethod = (typeof PAYMENT_METHODS)[number];

export const invoicePaymentSchema = z.object({
  id: z.string(),
  invoiceId: z.string(),
  amountMinor: z.number().int().positive(),
  method: z.enum(PAYMENT_METHODS).default('other'),
  reference: z.string().nullable().default(null),
  note: z.string().nullable().default(null),
  createdBy: z.string().nullable().default(null),
  createdAt: z.number(),
});
export type InvoicePayment = z.infer<typeof invoicePaymentSchema>;

export const invoicePaymentCreateSchema = invoicePaymentSchema.omit({ id: true, invoiceId: true, createdBy: true, createdAt: true });
export type InvoicePaymentCreate = z.infer<typeof invoicePaymentCreateSchema>;

/** Format minor units for display without pulling in a money library. */
export function formatMoney(minor: number, currency = 'USD', locale = getLocale()): string {
  return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(minor / 100);
}

/**
 * Charge a call against a rate, honouring minimum duration and billing
 * increments. Returns minor units to debit (always >= 0).
 */
export function rateCall(durationSec: number, rate: Pick<Rate, 'rateMinorPerMin' | 'setupFeeMinor' | 'minDurationSec' | 'incrementSec'>): number {
  if (durationSec <= 0) return 0;
  const billable = Math.max(durationSec, rate.minDurationSec);
  const increments = Math.ceil(billable / Math.max(1, rate.incrementSec));
  const billedSec = increments * Math.max(1, rate.incrementSec);
  return rate.setupFeeMinor + Math.round((billedSec / 60) * rate.rateMinorPerMin);
}

/* ------------------------------------------------------------------ */
/* Subscriptions                                                       */
/* ------------------------------------------------------------------ */

export const BILLING_INTERVALS = ['daily', 'weekly', 'monthly', 'quarterly', 'yearly'] as const;
export type BillingInterval = (typeof BILLING_INTERVALS)[number];

/** How many of each interval make up (roughly) one month — for normalising MRR. */
export const INTERVALS_PER_MONTH: Record<BillingInterval, number> = {
  daily: 365 / 12,
  weekly: 52 / 12,
  monthly: 1,
  quarterly: 1 / 3,
  yearly: 1 / 12,
};

export const SUBSCRIPTION_STATUSES = ['trialing', 'active', 'past_due', 'paused', 'cancelled'] as const;
export type SubscriptionStatus = (typeof SUBSCRIPTION_STATUSES)[number];

export const subscriptionPlanSchema = z.object({
  id: z.string(),
  name: z.string().min(1),
  description: z.string().default(''),
  currency: z.string().length(3).default('USD'),
  priceMinor: z.number().int().min(0).default(0),
  interval: z.enum(BILLING_INTERVALS).default('monthly'),
  /** What one unit is — "seat", "device", "channel". Shown on the invoice. */
  unitLabel: z.string().default('subscription'),
  /** Allowance included before overage applies (minutes, devices, seats). */
  includedQty: z.number().int().min(0).default(0),
  overageMinor: z.number().int().min(0).default(0),
  active: z.boolean().default(true),
  createdAt: z.number(),
  updatedAt: z.number(),
});
export type SubscriptionPlan = z.infer<typeof subscriptionPlanSchema>;

export const subscriptionPlanCreateSchema = subscriptionPlanSchema.omit({ id: true, createdAt: true, updatedAt: true });
export type SubscriptionPlanCreate = z.infer<typeof subscriptionPlanCreateSchema>;

export const subscriptionSchema = z.object({
  id: z.string(),
  customerId: z.string(),
  accountId: z.string().nullable().default(null),
  planId: z.string(),
  quantity: z.number().int().min(1).default(1),
  /** Negotiated price for this customer; null falls back to the plan price. */
  priceMinor: z.number().int().min(0).nullable().default(null),
  /**
   * What this subscription is billed in. Defaults to the plan's currency; a
   * different one needs its own price, since the plan price is not in it.
   */
  currency: z.string().length(3),
  status: z.enum(SUBSCRIPTION_STATUSES).default('active'),
  startedAt: z.number(),
  periodStart: z.number(),
  periodEnd: z.number(),
  cancelAt: z.number().nullable().default(null),
  cancelledAt: z.number().nullable().default(null),
  trialEndsAt: z.number().nullable().default(null),
  notes: z.string().nullable().default(null),
  createdAt: z.number(),
  updatedAt: z.number(),
});
export type Subscription = z.infer<typeof subscriptionSchema>;

export const subscriptionCreateSchema = subscriptionSchema
  .omit({ id: true, createdAt: true, updatedAt: true, cancelledAt: true, periodStart: true, periodEnd: true, startedAt: true })
  .partial({ accountId: true, priceMinor: true, currency: true, status: true, cancelAt: true, trialEndsAt: true, notes: true, quantity: true });
export type SubscriptionCreate = z.infer<typeof subscriptionCreateSchema>;

export interface InvoiceLine {
  id: string;
  invoiceId: string;
  description: string;
  quantity: number;
  unitPriceMinor: number;
  totalMinor: number;
  kind: 'subscription' | 'usage' | 'fee' | 'adjustment' | 'tax';
  subscriptionId: string | null;
  sortOrder: number;
  /** When the billed service was delivered — null on older lines, which fall back to the invoice period. */
  serviceStart: number | null;
  serviceEnd: number | null;
}

/**
 * A one-time charge waiting for (or already on) a client's invoice: a support
 * ticket fee, or anything else billed once with its own description. It stays
 * `pending` until the client's next invoice picks it up as a `fee` line, then
 * records which invoice billed it — so nothing is charged twice.
 */
export const ONE_TIME_CHARGE_KINDS = ['ticket_fee', 'other'] as const;
export type OneTimeChargeKind = (typeof ONE_TIME_CHARGE_KINDS)[number];

export interface OneTimeCharge {
  id: string;
  customerId: string;
  customerName: string | null;
  kind: OneTimeChargeKind;
  ticketId: string | null;
  /** Ticket reference, for display — null when the charge isn't tied to a ticket or the ticket is gone. */
  ticketReference: string | null;
  description: string;
  quantity: number;
  unitPriceMinor: number;
  totalMinor: number;
  currency: string;
  invoiceId: string | null;
  invoiceNumber: string | null;
  billedAt: number | null;
  createdBy: string | null;
  createdAt: number;
  updatedAt: number;
}

export const oneTimeChargeCreateSchema = z.object({
  customerId: z.string().min(1),
  kind: z.enum(ONE_TIME_CHARGE_KINDS).default('other'),
  ticketId: z.string().nullable().optional(),
  description: z.string().trim().min(1, 'A description is required').max(500),
  quantity: z.number().positive().max(100_000).default(1),
  unitPriceMinor: z.number().int().min(0),
});
export type OneTimeChargeCreate = z.infer<typeof oneTimeChargeCreateSchema>;

export const oneTimeChargeUpdateSchema = oneTimeChargeCreateSchema.pick({ description: true, quantity: true, unitPriceMinor: true }).partial();
export type OneTimeChargeUpdate = z.infer<typeof oneTimeChargeUpdateSchema>;

/** An ad-hoc line typed into the Generate invoice form — billed on that invoice only. */
export const extraInvoiceLineSchema = oneTimeChargeCreateSchema.pick({ description: true, quantity: true, unitPriceMinor: true });
export type ExtraInvoiceLine = z.infer<typeof extraInvoiceLineSchema>;

/**
 * The last day of service for display. Periods are stored half-open — a
 * month runs up to midnight on the 1st of the next — which would print as
 * "Aug 1 – Sep 1" on an invoice, so an end at exactly midnight UTC reads as
 * the day before.
 */
export function serviceEndForDisplay(start: number, end: number): number {
  return end > start && end % 86_400_000 === 0 ? end - 1 : end;
}

/** Advance a billing period by one interval, preserving the day of month. */
export function nextPeriodEnd(from: number, interval: BillingInterval): number {
  // Day/week periods are fixed-length and never need the month-clamp below.
  if (interval === 'daily') return from + 86_400_000;
  if (interval === 'weekly') return from + 7 * 86_400_000;

  const date = new Date(from);
  const day = date.getUTCDate();

  if (interval === 'monthly') date.setUTCMonth(date.getUTCMonth() + 1);
  else if (interval === 'quarterly') date.setUTCMonth(date.getUTCMonth() + 3);
  else date.setUTCFullYear(date.getUTCFullYear() + 1);

  // A subscription started on the 31st must not skip February. Clamping to the
  // last day of the shorter month keeps every renewal on the intended day.
  if (date.getUTCDate() < day) date.setUTCDate(0);
  return date.getTime();
}

/** Line total for a subscription, honouring quantity and any price override. */
export function subscriptionLineTotal(
  subscription: Pick<Subscription, 'quantity' | 'priceMinor'>,
  plan: Pick<SubscriptionPlan, 'priceMinor'>,
): number {
  const unit = subscription.priceMinor ?? plan.priceMinor;
  return unit * subscription.quantity;
}
